import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { AppUser, EditableRole, ModuleId, RbacConfig, Role, RolePermissions } from '../types/auth';
import { ADMIN_INTERNAL_IDS } from '../constants/users';
import { loadRbac, saveRbac as cacheRbac, normalizeRbac, currentKnownModules, DEFAULT_RBAC_CONFIG } from '../constants/rbac';
import { findEmployeeByInternalId } from '../constants/employees';
import * as rbacApi from '../services/rbacApi';

// Flameco global role (from the auth service token) → this app's Role. The
// token is authoritative for the BASE role; unknown values get the lowest role.
// Advanced holds everyone who was a Manager before 2026-10-15, so it keeps
// this app's Manager access.
function mapFlamecoRole(raw: string | undefined): Role {
  if (raw === 'Administrator') return 'Admin';
  if (raw === 'Manager' || raw === 'Advanced') return 'Manager';
  return 'Viewer';
}

// Token display name is "First Last" (used when the static roster lacks the person).
function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const parts = (name || '').trim().split(/\s+/);
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] || '' };
}

// NetSuite name format is "Last, First M". Convert to firstName/lastName.
function parseEmployeeName(name: string): { firstName: string; lastName: string } {
  const [last, rest] = name.split(',').map(s => s.trim());
  if (!rest) return { firstName: '', lastName: last || '' };
  return { firstName: rest, lastName: last };
}

// =============================================================================
// ROLE LAYER ON TOP OF AUTH
// -----------------------------------------------------------------------------
// Identity is owned by the login layer (AuthContext). This context adds the
// application role on top and now sources its config from the SHARED backend
// (/rbac-config) with a localStorage cache — so an Admin's changes to role
// permissions or user-role assignments reach every user on their next load.
//   - rolePermissions: which modules each editable role can access.
//   - userRoleOverrides: per-user role assignment layered over the login-derived
//     base role. A user whose base role is Admin can never be demoted via an
//     override (prevents admin lockout); non-admins take the override if set.
// =============================================================================

type RbacSaveState = 'idle' | 'saving' | 'saved' | 'error';
type RbacSource = 'loading' | 'server' | 'local';

interface RoleContextValue {
  appUser: AppUser | null;
  role: Role | null;
  isAdmin: boolean;
  permissions: RolePermissions;
  userRoleOverrides: Record<string, Role>;
  can: (moduleId: ModuleId) => boolean;
  unknownUser: boolean;
  // Persist a full new RBAC config to the shared backend (explicit Save).
  commitRbac: (next: RbacConfig) => void;
  resetRbacToDefaults: () => void;
  rbacSaveState: RbacSaveState;
  rbacSource: RbacSource;
  rbacError: string | null;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authSource, flamecoUser } = useAuth();

  // Shared RBAC config: instant paint from the localStorage cache, then reconcile
  // with the backend on mount.
  const [rbac, setRbac] = useState<RbacConfig>(() => loadRbac());
  const [rbacSaveState, setRbacSaveState] = useState<RbacSaveState>('idle');
  const [rbacSource, setRbacSource] = useState<RbacSource>('loading');
  const [rbacError, setRbacError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const server = await rbacApi.getConfig();
        if (cancelled) return;
        // Never clobber unsaved local edits an admin made while the request was
        // in flight; otherwise adopt the shared config.
        if (server && !dirty.current) {
          const normalized = normalizeRbac(server);
          setRbac(normalized);
          cacheRbac(normalized);
        }
        setRbacSource('server');
      } catch {
        if (cancelled) return;
        setRbacSource('local');
        setRbacError('Could not reach the server — using this browser’s cached roles.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  // Look up the signed-in user's BASE role (login-derived).
  const { baseAppUser, unknownUser } = useMemo(() => {
    if (authSource !== 'flameco' || !flamecoUser) {
      return { baseAppUser: null as AppUser | null, unknownUser: false };
    }
    // employeeId is the NetSuite internal id (the old shipping-app internalUser),
    // so the synthetic email below is the same key existing role overrides use.
    const id = flamecoUser.employeeId;
    const employee = findEmployeeByInternalId(id);
    const { firstName, lastName } = employee
      ? parseEmployeeName(employee.name)
      : splitDisplayName(flamecoUser.name);
    const role: Role = ADMIN_INTERNAL_IDS.has(id) ? 'Admin' : mapFlamecoRole(flamecoUser.role);
    return {
      baseAppUser: { firstName, lastName, email: `${id}@netsuite.flameco`, role } as AppUser,
      // The auth service vouches for everyone who signs in.
      unknownUser: false,
    };
  }, [authSource, flamecoUser]);

  // Apply the per-user override on top of the base role. Base admins are locked
  // (can't be demoted); everyone else takes their override if one is set.
  const appUser = useMemo<AppUser | null>(() => {
    if (!baseAppUser) return null;
    if (baseAppUser.role === 'Admin') return baseAppUser;
    const override = rbac.userRoleOverrides[baseAppUser.email.toLowerCase()];
    return override ? { ...baseAppUser, role: override } : baseAppUser;
  }, [baseAppUser, rbac.userRoleOverrides]);

  const role = appUser?.role ?? null;
  const isAdmin = role === 'Admin';
  const permissions = rbac.rolePermissions;

  // Persist a new config to the shared backend (immediate — used by the explicit
  // Save in User Management). Optimistically caches + applies locally first.
  const commitRbac = useCallback((next: RbacConfig) => {
    dirty.current = true;
    // Persist the admin's explicit choices verbatim — only (re)stamp the
    // known-modules snapshot. (normalizeRbac's merge-in-defaults is load-time
    // only; running it here would re-add modules the admin just unchecked.)
    const stamped: RbacConfig = {
      rolePermissions: next.rolePermissions,
      userRoleOverrides: next.userRoleOverrides,
      knownModules: currentKnownModules(),
    };
    setRbac(stamped);
    cacheRbac(stamped);
    setRbacSaveState('saving');
    const email = appUser?.email;
    (async () => {
      try {
        await rbacApi.saveConfig(stamped, email);
        setRbacSaveState('saved');
        setRbacSource('server');
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setRbacSaveState('idle'), 1500);
      } catch {
        setRbacSaveState('error');
        setRbacSource('local');
      }
    })();
  }, [appUser?.email]);

  const resetRbacToDefaults = useCallback(() => {
    commitRbac(DEFAULT_RBAC_CONFIG);
  }, [commitRbac]);

  const can = useCallback((moduleId: ModuleId): boolean => {
    if (!role) return false;
    if (role === 'Admin') return true;
    if (moduleId === 'user-management') return false;
    return permissions[role as EditableRole]?.includes(moduleId) ?? false;
  }, [role, permissions]);

  const value: RoleContextValue = {
    appUser, role, isAdmin, permissions,
    userRoleOverrides: rbac.userRoleOverrides,
    can, unknownUser,
    commitRbac, resetRbacToDefaults, rbacSaveState, rbacSource, rbacError,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = (): RoleContextValue => {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used inside <RoleProvider>');
  return ctx;
};
